'use client';

import { useMemo, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { useActiveTenantId } from '../components/tenant/TenantProvider';
import {
  Permission,
  hasPermission,
  hasAnyPermission,
  canSeeNavItem,
} from '@auctogon/core-admin/api/permissions';
import { grantToPermission, type Grant } from '@auctogon/core-admin/api/grants';

/**
 * Client-side hook for checking user permissions.
 *
 * BUG-211: gating is scoped to the ACTIVE tenant. `roles` is the user's detailed
 * roles AT the current tenant (from `tenantRoles[activeTenantId]`). When there's
 * no active tenant (no provider / not yet resolved) roles resolve to `[]` — a
 * transient pre-hydration state; the global `staff_users.roles` fallback was
 * removed with the column (PR-3). Super-admins bypass regardless.
 */
export function usePermissions() {
  const { tenantRoles, isSuperAdmin } = useAuth();
  const activeTenantId = useActiveTenantId();

  const roles = useMemo(
    () => (activeTenantId ? (tenantRoles[activeTenantId] ?? []) : []),
    [activeTenantId, tenantRoles]
  );

  /** Check if the user has a specific permission at the active tenant. */
  const can = useCallback(
    (permission: Permission) => hasPermission(roles, permission, isSuperAdmin),
    [roles, isSuperAdmin]
  );

  /** Check if the user has ANY of the given permissions at the active tenant. */
  const canAny = useCallback(
    (permissions: readonly Permission[]) => hasAnyPermission(roles, permissions, isSuperAdmin),
    [roles, isSuperAdmin]
  );

  /** Check if a sidebar nav item should be visible. */
  const canSeeNav = useCallback(
    (href: string) => canSeeNavItem(href, roles, isSuperAdmin),
    [roles, isSuperAdmin]
  );

  return useMemo(
    () => ({ can, canAny, canSeeNav, roles, isSuperAdmin }),
    [can, canAny, canSeeNav, roles, isSuperAdmin]
  );
}

/**
 * What opens a page, named once so the check and the No access copy read the same
 * list (ADR 062 — Access Grants).
 *
 * - `grants`: holding any of these grants opens it; the copy names their one resource.
 * - `permissions`: holding any of these permissions opens it, worded with `area`. For a
 *   page with no one grant behind it: the clerk console's `clerking.view`, or a set the
 *   sidebar also reads (`AUCTIONS_LIST_VIEW_PERMISSIONS`, `OFFLINE_PAGE_PERMISSIONS`).
 */
export type PageAccess =
  | { kind: 'grants'; grants: readonly Grant[] }
  | { kind: 'permissions'; area: string; permissions: readonly Permission[] };

/**
 * The permissions that open a page or tab, holding any one being enough: a grant
 * access reads through `grantToPermission`, the route gate's own mapping. BUG-2133:
 * shared by `usePageAccess` and the auction tab strip's padlocks, so a tab and the
 * panel it opens can't disagree.
 *
 * @param access - what opens the page.
 * @returns The permission set.
 */
export function pageAccessPermissions(access: PageAccess): readonly Permission[] {
  return access.kind === 'grants' ? access.grants.map(grantToPermission) : access.permissions;
}

/**
 * Whether the viewer may open a page, for a page that renders its No access panel in
 * place (BL-2503, ADR 030 — Design Canon, "Role-gated UI is locked, not hidden"). It
 * never redirects: BL-2559 deleted the two hooks that bounced a viewer to Home. Pass
 * the same `access` to `NoAccessPanel`. The route gate still
 * decides every call (ADR 062 — Access Grants).
 *
 * @param access - what opens the page.
 * @returns `loading` while the session resolves; `allowed` at the active house once it
 *   has; and `allowedInAnyHouse`, which a record page reads to open a record from another
 *   of the viewer's houses before it adopts that house (BUG-2186).
 */
export function usePageAccess(access: PageAccess): { allowed: boolean; loading: boolean; allowedInAnyHouse: boolean } {
  const { loading, tenantRoles } = useAuth();
  const { canAny, isSuperAdmin } = usePermissions();
  const permissions = pageAccessPermissions(access);
  const allowed = canAny(permissions);
  const allowedInAnyHouse =
    allowed || Object.values(tenantRoles).some((roles) => hasAnyPermission(roles, permissions, isSuperAdmin));
  return { allowed, loading, allowedInAnyHouse };
}
